import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Modal,
  Form,
  Tabs,
  Row,
  Col,
  Statistic,
  Tree,
  List,
  Badge,
  Tooltip,
  message,
  Popconfirm,
  Upload,
  Alert,
  Steps,
  Radio,
  Switch,
  InputNumber,
  Divider,
  Descriptions,
  Progress,
} from 'antd';
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  BookOutlined,
  FolderOutlined,
  FileTextOutlined,
  ReloadOutlined,
  ExportOutlined,
  ImportOutlined,
  FilterOutlined,
  UploadOutlined,
  DownloadOutlined,
  CopyOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  SyncOutlined,
} from '@ant-design/icons';

const { Option } = Select;
const { TextArea } = Input;

const KnowledgeBase: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [syncModalVisible, setSyncModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [syncProgress, setSyncProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedAgent, setSelectedAgent] = useState<string | null>(null);
  const [categoryModalVisible, setCategoryModalVisible] = useState(false);
  const [categoryForm] = Form.useForm();
  // 模拟智能体与按智能体划分的分类树
  const agents = [
    { id: 'bot-1', name: '客服Bot' },
    { id: 'bot-2', name: '游戏介绍Bot' },
    { id: 'bot-3', name: '充值助手Bot' },
  ];
  const [agentCategoryTree, setAgentCategoryTree] = useState<Record<string, any[]>>({
    'bot-1': [
      {
        title: '平台相关', key: 'p-1', children: [
          { title: '支付相关', key: 'p-1-1' },
          { title: '账户相关', key: 'p-1-2' },
        ]
      }
    ],
    'bot-2': [
      {
        title: '游戏相关', key: 'g-1', children: [
          { title: '游戏规则', key: 'g-1-1' },
          { title: '游戏技巧', key: 'g-1-2' },
        ]
      }
    ],
    'bot-3': [
      {
        title: '支付与财务', key: 'f-1', children: [
          { title: '充值方式', key: 'f-1-1' },
          { title: '退款说明', key: 'f-1-2' },
        ]
      }
    ],
  });

  // 新增知识条目
  const handleAddKnowledge = () => {
    setSelectedRecord(null);
    form.resetFields();
    setEditModalVisible(true);
  };

  // 编辑知识条目
  const handleEditKnowledge = (record: any) => {
    setSelectedRecord(record);
    form.setFieldsValue(record);
    setEditModalVisible(true);
  };

  // 保存知识条目
  const handleSaveKnowledge = async (values: any) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      if (selectedRecord) {
        message.success('知识条目更新成功！');
      } else {
        message.success('知识条目创建成功！');
      }
      setEditModalVisible(false);
      form.resetFields();
    } catch (error) {
      message.error('保存失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 删除知识条目
  const handleDeleteKnowledge = async (record: any) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      message.success(`已删除知识条目：${record.title}`);
    } catch (error) {
      message.error('删除失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 查看知识详情
  const handleViewKnowledge = (record: any) => {
    setSelectedRecord(record);
    setViewModalVisible(true);
  };

  // 复制知识条目
  const handleCopyKnowledge = async (record: any) => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      message.success(`已复制知识条目：${record.title}`);
    } catch (error) {
      message.error('复制失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 批量删除
  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请选择要删除的知识条目');
      return;
    }
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      message.success(`已删除 ${selectedRowKeys.length} 个知识条目`);
      setSelectedRowKeys([]);
    } catch (error) {
      message.error('批量删除失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 导入知识库
  const handleImportKnowledge = () => {
    setImportModalVisible(true);
  };

  // 导出知识库
  const handleExportKnowledge = async () => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      message.success('知识库导出成功！');
    } catch (error) {
      message.error('导出失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 同步知识库
  const handleSyncKnowledge = async () => {
    setSyncModalVisible(true);
    setSyncProgress(0);
    setCurrentStep(0);
    
    const steps = [
      { step: 0, title: '检查知识库状态', status: 'process' },
      { step: 1, title: '更新向量索引', status: 'process' },
      { step: 2, title: '同步到AI模型', status: 'process' },
      { step: 3, title: '验证同步结果', status: 'finish' },
    ];
    
    for (let i = 0; i < steps.length; i++) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setCurrentStep(i);
      setSyncProgress((i + 1) * 25);
    }
    
    message.success('知识库同步完成！');
  };

  const knowledgeData = [
    {
      id: 1,
      title: '老虎机游戏规则',
      category: '游戏规则',
      agentId: 'bot-2',
      content: '老虎机是一款经典的博彩游戏...',
      keywords: ['老虎机', '游戏规则', '玩法'],
      status: 'published',
      views: 1256,
      lastUpdated: '2025-10-23',
    },
    {
      id: 2,
      title: '充值方式说明',
      category: '支付相关',
      agentId: 'bot-3',
      content: '我们支持多种充值方式...',
      keywords: ['充值', '支付', '方式'],
      status: 'published',
      views: 856,
      lastUpdated: '2025-10-23',
    },
    {
      id: 3,
      title: '账户找回流程',
      category: '账户相关',
      agentId: 'bot-1',
      content: '若忘记密码，可通过绑定手机或邮箱找回账户...',
      keywords: ['账户', '密码', '找回'],
      status: 'published',
      views: 632,
      lastUpdated: '2025-10-22',
    },
    {
      id: 4,
      title: '德州扑克基础规则',
      category: '游戏规则',
      agentId: 'bot-2',
      content: '德州扑克使用标准52张扑克牌，玩家比大小...',
      keywords: ['德州扑克', '规则', '扑克牌'],
      status: 'draft',
      views: 210,
      lastUpdated: '2025-10-21',
    },
    {
      id: 5,
      title: '快速充值指引',
      category: '支付相关',
      agentId: 'bot-3',
      content: '快捷充值支持银行卡、第三方支付...',
      keywords: ['充值', '快捷', '支付'],
      status: 'published',
      views: 980,
      lastUpdated: '2025-10-20',
    },
    {
      id: 6,
      title: '新手游戏技巧合集',
      category: '游戏规则',
      agentId: 'bot-2',
      content: '新手可从低倍开始，逐步熟悉规则与赔率...',
      keywords: ['新手', '技巧', '赔率'],
      status: 'published',
      views: 432,
      lastUpdated: '2025-10-19',
    },
    {
      id: 7,
      title: '退款申请须知',
      category: '支付相关',
      agentId: 'bot-3',
      content: '退款需满足相应条件并提交材料，审核通过后原路退回...',
      keywords: ['退款', '审核', '支付'],
      status: 'draft',
      views: 88,
      lastUpdated: '2025-10-18',
    },
    {
      id: 8,
      title: '平台活动规则总览',
      category: '活动相关',
      agentId: 'bot-1',
      content: '平台不定期推出返利与抽奖活动，参与需阅读详情...',
      keywords: ['活动', '返利', '抽奖'],
      status: 'published',
      views: 512,
      lastUpdated: '2025-10-18',
    },
    {
      id: 9,
      title: '账号安全建议',
      category: '账户相关',
      agentId: 'bot-1',
      content: '请启用两步验证并定期更换密码，谨防钓鱼链接...',
      keywords: ['安全', '两步验证', '账号'],
      status: 'published',
      views: 743,
      lastUpdated: '2025-10-17',
    },
    {
      id: 10,
      title: '常见支付失败原因',
      category: '支付相关',
      agentId: 'bot-3',
      content: '支付失败可能因网络、限额、风控等原因导致...',
      keywords: ['支付失败', '限额', '风控'],
      status: 'published',
      views: 405,
      lastUpdated: '2025-10-16',
    },
  ];

  // 级联与搜索：
  // - 若搜索有关键字：忽略分类/智能体限制，按关键字查询
  // - 否则：未选择智能体或未选择具体分类（=all） => 展示空；已选则按分类展示
  const displayedKnowledge = (() => {
    const kw = (searchText || '').trim();
    if (kw.length > 0) {
      const lower = kw.toLowerCase();
      return knowledgeData.filter(item =>
        item.title.toLowerCase().includes(lower) ||
        item.content.toLowerCase().includes(lower) ||
        (item.keywords || []).some((k: string) => (k || '').toLowerCase().includes(lower))
      );
    }
    if (!selectedAgent || filterCategory === 'all') return [];
    return knowledgeData.filter((item) => item.category === filterCategory && item.agentId === selectedAgent);
  })();

  const columns = [
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      render: (title: string) => (
        <Space>
          <FileTextOutlined />
          {title}
        </Space>
      ),
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      render: (category: string) => <Tag color="blue">{category}</Tag>,
    },
    {
      title: '关键词',
      dataIndex: 'keywords',
      key: 'keywords',
      render: (keywords: string[]) => (
        <Space wrap>
          {keywords.map((keyword, index) => (
            <Tag key={index}>{keyword}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: '浏览量',
      dataIndex: 'views',
      key: 'views',
      sorter: (a: any, b: any) => a.views - b.views,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'published' ? 'green' : 'orange'}>
          {status === 'published' ? '已发布' : '草稿'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: any) => (
        <Space>
          <Tooltip title="查看详情">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => handleViewKnowledge(record)}
            >
              查看
            </Button>
          </Tooltip>
          <Tooltip title="编辑知识">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEditKnowledge(record)}
            >
              编辑
            </Button>
          </Tooltip>
          <Tooltip title="复制知识">
            <Button
              type="link"
              icon={<CopyOutlined />}
              onClick={() => handleCopyKnowledge(record)}
            >
              复制
            </Button>
          </Tooltip>
          <Popconfirm
            title="确定要删除这个知识条目吗？"
            onConfirm={() => handleDeleteKnowledge(record)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除知识">
              <Button
                type="link"
                icon={<DeleteOutlined />}
                danger
              >
                删除
              </Button>
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>知识库管理</h1>
        <p>编辑自有的知识库，完善智能体的知识体系，支持多种知识类型的结构化管理</p>
      </div>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={6}>
          <Card title="知识分类" size="small" extra={<Button type="link" onClick={() => {
            if (!selectedAgent) { message.warning('请先选择智能体'); return; }
            categoryForm.resetFields(); setCategoryModalVisible(true);
          }}>新增分类</Button>}>
            <div style={{ marginBottom: 12 }}>
              <Select
                placeholder="请选择智能体（必选）"
                style={{ width: '100%' }}
                value={selectedAgent as any}
                onChange={(v) => { setSelectedAgent(v); setFilterCategory('all'); }}
              >
                {agents.map(a => (
                  <Option key={a.id} value={a.id}>{a.name}</Option>
                ))}
              </Select>
            </div>
            <Alert type="info" showIcon style={{ marginBottom: 12 }} message={selectedAgent ? '已选择智能体，可管理其知识分类' : '请选择智能体后再管理分类'} />
            <Tree
              defaultExpandedKeys={["root-1","root-2"]}
              treeData={selectedAgent ? (agentCategoryTree[selectedAgent] || []) : []}
              onSelect={(_, info: any) => {
                if (info?.node?.children && info.node.children.length > 0) {
                  setFilterCategory('all');
                } else if (info?.node?.title) {
                  setFilterCategory(info.node.title);
                }
              }}
            />
          </Card>
        </Col>
        <Col xs={24} lg={18}>
          <Card
            title="知识条目"
            extra={
              <Space>
                <Tooltip title="导入知识库">
                  <Button icon={<ImportOutlined />} onClick={handleImportKnowledge}>
                    导入
                  </Button>
                </Tooltip>
                <Tooltip title="导出知识库">
                  <Button icon={<ExportOutlined />} onClick={handleExportKnowledge}>
                    导出
                  </Button>
                </Tooltip>
                <Tooltip title="同步知识库">
                  <Button icon={<SyncOutlined />} onClick={handleSyncKnowledge}>
                    同步
                  </Button>
                </Tooltip>
                <Tooltip title="新增知识">
                  <Button type="primary" icon={<PlusOutlined />} onClick={handleAddKnowledge}>
                    新增知识
                  </Button>
                </Tooltip>
              </Space>
            }
          >
            <div style={{ marginBottom: 16 }}>
              <Space>
                <Input
                  placeholder="搜索知识标题或内容"
                  prefix={<SearchOutlined />}
                  style={{ width: 200 }}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
                {/* 移除右侧分类选择，由左侧树控制分类级联 */}
                <Select 
                  placeholder="状态" 
                  style={{ width: 100 }}
                  value={filterStatus}
                  onChange={setFilterStatus}
                >
                  <Option value="all">全部状态</Option>
                  <Option value="published">已发布</Option>
                  <Option value="draft">草稿</Option>
                </Select>
                <Tooltip title="刷新数据">
                  <Button 
                    icon={<ReloadOutlined />} 
                    onClick={() => message.info('数据已刷新')}
                  />
                </Tooltip>
                <Tooltip title="高级筛选">
                  <Button icon={<FilterOutlined />} />
                </Tooltip>
              </Space>
            </div>

            <Table
              rowSelection={{
                selectedRowKeys,
                onChange: setSelectedRowKeys,
              }}
              columns={columns}
              dataSource={displayedKnowledge}
              rowKey="id"
              onRow={(record: any) => ({
                onClick: () => {
                  if (record?.agentId) setSelectedAgent(record.agentId);
                  if (record?.category) setFilterCategory(record.category);
                }
              })}
              scroll={{ x: 1400 }}
              pagination={{
                total: displayedKnowledge.length,
                pageSize: 10,
                showTotal: (total) => `共 ${total} 条知识`,
              }}
            />

            {/* 批量操作 */}
            {selectedRowKeys.length > 0 && (
              <Card style={{ marginTop: 16 }}>
                <Space>
                  <span>已选择 {selectedRowKeys.length} 条知识</span>
                  <Tooltip title="批量发布">
                    <Button icon={<CheckCircleOutlined />} onClick={() => message.info('批量发布功能开发中...')}>
                      批量发布
                    </Button>
                  </Tooltip>
                  <Tooltip title="批量设为草稿">
                    <Button icon={<FileTextOutlined />} onClick={() => message.info('批量设为草稿功能开发中...')}>
                      设为草稿
                    </Button>
                  </Tooltip>
                  <Popconfirm
                    title={`确定要删除选中的 ${selectedRowKeys.length} 条知识吗？`}
                    onConfirm={handleBatchDelete}
                    okText="确定"
                    cancelText="取消"
                  >
                    <Tooltip title="批量删除">
                      <Button icon={<DeleteOutlined />} danger>
                        批量删除
                      </Button>
                    </Tooltip>
                  </Popconfirm>
                </Space>
              </Card>
            )}
          </Card>
        </Col>
      </Row>

      {/* 编辑知识模态框 */}
      <Modal
        title={selectedRecord ? '编辑知识' : '新增知识'}
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        width={800}
        footer={[
          <Button key="cancel" onClick={() => setEditModalVisible(false)}>
            取消
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={loading}
            onClick={() => form.submit()}
          >
            保存
          </Button>
        ]}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveKnowledge}
        >
          <Form.Item
            name="title"
            label="知识标题"
            rules={[{ required: true, message: '请输入知识标题' }]}
          >
            <Input placeholder="请输入知识标题" />
          </Form.Item>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="category1"
                label="一级分类"
                rules={[{ required: true, message: '请选择一级分类' }]}
              >
                <Select placeholder="请选择一级分类">
                  <Option value="平台相关">平台相关</Option>
                  <Option value="游戏相关">游戏相关</Option>
                  <Option value="支付与财务">支付与财务</Option>
                  <Option value="活动相关">活动相关</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="category2"
                label="二级分类"
              >
                <Select placeholder="请选择二级分类">
                  <Option value="支付相关">支付相关</Option>
                  <Option value="账户相关">账户相关</Option>
                  <Option value="游戏规则">游戏规则</Option>
                  <Option value="游戏技巧">游戏技巧</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="agentId"
            label="归属智能体（必选）"
            rules={[{ required: true, message: '请选择归属智能体' }]}
          >
            <Select placeholder="请选择归属智能体">
              {agents.map(a => (
                <Option key={a.id} value={a.id}>{a.name}</Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="importance"
                label="重要度"
                rules={[{ required: true, message: '请选择重要度' }]}
              >
                <Select placeholder="1-10">
                  {[10,9,8,7,6,5,4,3,2,1].map(v => (
                    <Option key={v} value={v}>{v}分</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="credibility"
                label="可信度"
                rules={[{ required: true, message: '请选择可信度' }]}
              >
                <Select placeholder="1-10">
                  {[10,9,8,7,6,5,4,3,2,1].map(v => (
                    <Option key={v} value={v}>{v}分</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="freshness"
                label="新鲜度"
                rules={[{ required: true, message: '请选择新鲜度' }]}
              >
                <Select placeholder="1-10">
                  {[10,9,8,7,6,5,4,3,2,1].map(v => (
                    <Option key={v} value={v}>{v}分</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="content"
            label="知识内容"
            rules={[{ required: true, message: '请输入知识内容' }]}
          >
            <TextArea rows={8} placeholder="请输入知识内容" />
          </Form.Item>
          
          <Form.Item
            name="keywords"
            label="关键词"
          >
            <Select
              mode="tags"
              placeholder="输入关键词，按回车添加"
              style={{ width: '100%' }}
            />
          </Form.Item>
          
          <Form.Item
            name="status"
            label="发布状态"
            initialValue="draft"
          >
            <Radio.Group>
              <Radio value="draft">草稿</Radio>
              <Radio value="published">发布</Radio>
            </Radio.Group>
          </Form.Item>
        </Form>
      </Modal>

      {/* 查看知识详情模态框 */}
      <Modal
        title="知识详情"
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        width={700}
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            关闭
          </Button>
        ]}
      >
        {selectedRecord && (
          <div>
            <Descriptions column={2} bordered>
              <Descriptions.Item label="标题" span={2}>
                {selectedRecord.title}
              </Descriptions.Item>
              <Descriptions.Item label="分类">
                <Tag color="blue">{selectedRecord.category}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="状态">
                <Tag color={selectedRecord.status === 'published' ? 'green' : 'orange'}>
                  {selectedRecord.status === 'published' ? '已发布' : '草稿'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="浏览次数">
                {selectedRecord.views}
              </Descriptions.Item>
              <Descriptions.Item label="最后更新">
                {selectedRecord.lastUpdated}
              </Descriptions.Item>
              <Descriptions.Item label="关键词" span={2}>
                {selectedRecord.keywords?.map((keyword: string) => (
                  <Tag key={keyword} color="purple">{keyword}</Tag>
                ))}
              </Descriptions.Item>
              <Descriptions.Item label="内容" span={2}>
                <div style={{ 
                  maxHeight: '200px', 
                  overflow: 'auto',
                  padding: '12px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '4px'
                }}>
                  {selectedRecord.content}
                </div>
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Modal>

      {/* 导入知识库模态框 */}
      <Modal
        title="导入知识库"
        open={importModalVisible}
        onCancel={() => setImportModalVisible(false)}
        width={600}
        footer={[
          <Button key="cancel" onClick={() => setImportModalVisible(false)}>
            取消
          </Button>,
          <Button key="import" type="primary" loading={loading}>
            开始导入
          </Button>
        ]}
      >
        <div>
          <Alert
            message="导入说明"
            description="支持导入 Markdown、Word、PDF 等格式的文档，系统将自动解析并创建知识条目。"
            type="info"
            style={{ marginBottom: 16 }}
          />
          
          <Tabs defaultActiveKey="1">
            <Tabs.TabPane tab="文件上传" key="1">
              <Upload.Dragger
                name="files"
                multiple
                action="/api/upload"
                accept=".md,.docx,.pdf,.txt"
              >
                <p className="ant-upload-drag-icon">
                  <UploadOutlined />
                </p>
                <p className="ant-upload-text">点击或拖拽文件到此区域上传</p>
                <p className="ant-upload-hint">
                  支持 Markdown、Word、PDF、TXT 格式
                </p>
              </Upload.Dragger>
            </Tabs.TabPane>
            
            <Tabs.TabPane tab="批量导入" key="2">
              <Form layout="vertical">
                <Form.Item label="导入格式">
                  <Radio.Group defaultValue="json">
                    <Radio value="json">JSON</Radio>
                    <Radio value="csv">CSV</Radio>
                    <Radio value="excel">Excel</Radio>
                  </Radio.Group>
                </Form.Item>
                
                <Form.Item label="模板下载">
                  <Space>
                    <Button icon={<DownloadOutlined />}>下载JSON模板</Button>
                    <Button icon={<DownloadOutlined />}>下载CSV模板</Button>
                    <Button icon={<DownloadOutlined />}>下载Excel模板</Button>
                  </Space>
                </Form.Item>
              </Form>
            </Tabs.TabPane>
          </Tabs>
        </div>
      </Modal>

      {/* 新增分类 */}
      <Modal
        title="新增分类"
        open={categoryModalVisible}
        onCancel={() => setCategoryModalVisible(false)}
        okText="创建"
        onOk={() => categoryForm.submit()}
      >
        <Form form={categoryForm} layout="vertical" onFinish={(vals) => {
          if (!selectedAgent) { message.warning('请先选择智能体'); return; }
          const { name, parentKey } = vals;
          const tree = agentCategoryTree[selectedAgent] || [];
          const newNode = { title: name, key: `${Date.now()}` };
          const insertChild = (nodes: any[]): boolean => {
            for (const n of nodes) {
              if (n.key === parentKey) {
                n.children = n.children || [];
                n.children.push(newNode);
                return true;
              }
              if (n.children && insertChild(n.children)) return true;
            }
            return false;
          };
          if (parentKey) {
            if (!insertChild(tree)) {
              tree.push(newNode);
            }
          } else {
            tree.push(newNode);
          }
          setAgentCategoryTree({ ...agentCategoryTree, [selectedAgent]: [...tree] });
          message.success('分类创建成功');
          setCategoryModalVisible(false);
        }}>
          <Form.Item label="归属智能体" required>
            <Select value={selectedAgent as any} disabled>
              {agents.map(a => (
                <Option key={a.id} value={a.id}>{a.name}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="name" label="分类名称" rules={[{ required: true, message: '请输入分类名称' }]}>
            <Input placeholder="请输入分类名称" />
          </Form.Item>
          <Form.Item name="parentKey" label="上级分类（可选）">
            <Select allowClear placeholder="选择上级分类">
              {(selectedAgent ? (agentCategoryTree[selectedAgent] || []) : []).map((root: any) => (
                <Option key={root.key} value={root.key}>{root.title}</Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 同步知识库模态框 */}
      <Modal
        title="同步知识库"
        open={syncModalVisible}
        onCancel={() => setSyncModalVisible(false)}
        width={600}
        footer={[
          <Button key="close" onClick={() => setSyncModalVisible(false)}>
            关闭
          </Button>
        ]}
      >
        <div>
          <Alert
            message="同步进度"
            description="正在将知识库同步到AI模型，请稍候..."
            type="info"
            style={{ marginBottom: 16 }}
          />
          
          <Steps
            current={currentStep}
            items={[
              { title: '检查知识库状态' },
              { title: '更新向量索引' },
              { title: '同步到AI模型' },
              { title: '验证同步结果' },
            ]}
            style={{ marginBottom: 24 }}
          />
          
          <div style={{ textAlign: 'center' }}>
            <Progress
              type="circle"
              percent={syncProgress}
              format={() => `${syncProgress}%`}
            />
          </div>
          
          {syncProgress === 100 && (
            <Alert
              message="同步完成"
              description="知识库已成功同步到AI模型，可以开始使用新的知识内容。"
              type="success"
              style={{ marginTop: 16 }}
            />
          )}
        </div>
      </Modal>
    </div>
  );
};

export default KnowledgeBase;
